import React, { useState } from 'react';
import { galleryProjects } from '../mockData';
import { MapPin, X, ExternalLink } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      {/* Page Header with Modern Design */}
      <section className="relative bg-gradient-to-br from-[#222] via-[#2a2a2a] to-[#1a1a1a] text-white py-24 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #C1A57B 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Decorative blurs */}
        <div className="absolute top-10 right-10 w-72 h-72 bg-[#C1A57B]/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#C1A57B]/5 rounded-full blur-3xl animate-float-delayed"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-4">Our Projects</h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#C1A57B] to-transparent mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of successful office furniture installations across India
          </p>
        </div>
      </section>

      {/* Gallery Grid with Advanced Hover Effects */}
      <section className="py-20 bg-gradient-to-b from-[#EDEDED] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryProjects.map((project, index) => (
              <ScrollReveal key={project.id} delay={index * 100}>
                <div
                  onClick={() => setSelectedImage(project)}
                  className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#222]/95 via-[#222]/50 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500"></div>
                    
                    {/* Hover icon */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-500 scale-0 group-hover:scale-100">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border-2 border-white/30">
                        <ExternalLink size={28} className="text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <h3 className="text-2xl font-bold mb-2 group-hover:text-[#C1A57B] transition-colors duration-300">{project.title}</h3>
                    <div className="flex items-center text-sm text-gray-300 mb-3">
                      <MapPin size={16} className="mr-2 text-[#C1A57B]" />
                      {project.location}
                    </div>
                    <p className="text-sm text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeInUp"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 p-3 bg-white/10 backdrop-blur-md rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110 group"
          >
            <X size={24} className="text-white group-hover:rotate-90 transition-transform duration-300" />
          </button>
          
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-6">
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full h-auto"
              />
            </div>
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
              <h3 className="text-3xl font-bold text-white mb-3">{selectedImage.title}</h3>
              <div className="flex items-center text-[#C1A57B] mb-4">
                <MapPin size={20} className="mr-2" />
                <span className="text-lg">{selectedImage.location}</span>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#C1A57B] via-[#D4B896] to-[#C1A57B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 animate-shimmer"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollReveal>
            <h2 className="text-4xl font-bold text-white mb-4">Want to See Your Project Here?</h2>
            <p className="text-xl text-white/95 mb-8">
              Let's collaborate to create an inspiring workspace for your team
            </p>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-white text-[#222] font-bold rounded-xl hover:bg-[#EDEDED] transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              Start Your Project
            </a>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Gallery;